import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Button from 'components/ui/button';
import { useAuth } from 'context/AuthContext';
import { actionLog } from 'services/actionlog.service';
import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/storage';
import { checkInvite } from 'services/dashboard.service';
import { getRegulation, IRegulation } from 'services/regulation.service';
import { ModalFrame } from 'components/ui/modal-frame';
import { canRenounceDynamics } from 'pages/Preference/communication-preferences';

import { cycleBadge } from './cycle-badge';
import { RegulationReader } from './regulation-reader';

// interface RegulamentoModalProps {
//   on
// }

// D3: 17px não tem degrau no Tailwind.
const CHECKBOX = 'h-[17px] w-[17px] shrink-0 cursor-pointer accent-[var(--brand-blue)]';

interface CheckboxRowProps {
  children: ReactNode;
}

/**
 * Caixa amarela do aceite, como no rodapé do modal de regulamento do `Home.dc.html`. O destaque
 * vai em `brand-blue-dark`: o `brand-blue` do design sobre o creme fica em 4,46:1, abaixo do AA.
 */
function AgreementCheckbox({ children, onChange }: CheckboxRowProps & { onChange: (e: ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <label
      // D3: 11px e o raio de 10px não têm degrau no Tailwind.
      className="flex min-w-[min(300px,100%)] flex-1 cursor-pointer items-center gap-[11px] rounded-[10px] border-2 border-brand-yellow bg-brand-yellow-light px-4 py-3 text-jj-body-sm text-content"
    >
      <input id="accept" type="checkbox" name="accept" className={CHECKBOX} onChange={onChange} />
      <span>{children}</span>
    </label>
  );
}

function DynamicCheckbox({
  name,
  checked,
  onChange,
  children,
}: CheckboxRowProps & { name: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label
      // D3: 11px e o raio de 10px não têm degrau no Tailwind.
      className="flex cursor-pointer items-center gap-[11px] rounded-[10px] border border-surface-200 px-4 py-3"
    >
      <input type="checkbox" name={name} className={CHECKBOX} checked={checked} onChange={e => onChange(e.target.checked)} />
      {children}
    </label>
  );
}

function FooterActions({ children }: CheckboxRowProps) {
  return <div className="flex w-full shrink-0 gap-2.5 sm:w-auto">{children}</div>;
}

export function RegulamentoModal(props: any) {
  // const modalID = "achievement";

  // const modalID = 'help-pontos';

  const [modalIsOpen, setIsOpen] = useState(false);
  const [isAccept, setIsAccept] = useState(false);

  const { signOut, refreshUserPromise, user, userInfo, realCycle } = useAuth();

  // const { openModal, closeModal } = useContext(ModalContext);

  const [accept, setAccept] = useState(false);
  /** `actions` é o nome usado no payload, em `player.extra` e no produtor (D-33). */
  const [actions, setActions] = useState(true);
  const [challengeDecline, setChallengeDecline] = useState(true);
  const [task, setTask] = useState(true);
  const [taskDecline, setTaskDecline] = useState(true);
  const [recognition, setRecognition] = useState(true);
  const [recognitionDecline, setRecognitionDecline] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [decline, setDecline] = useState(false);
  const [regulation, setRegulation] = useState<IRegulation>();
  const [willOpen, setWillOpen] = useState(true);

  /**
   * Quem renuncia a dinâmica vê as preferências; quem não renuncia vê o
   * regulamento (PREF-04.3). Era um `step` no código antigo, mas nunca foi
   * sequência — o passo 1 já terminava em "Salvar Preferências" e o 2 só era
   * alcançado por quem pulava direto. Ramo por perfil diz o que sempre foi.
   */
  const showRenounce = canRenounceDynamics({
    isManager: !!userInfo?.is_manager,
    cadMissao: userInfo?.cad_missao,
    tipoRo: userInfo?.tipo_ro,
  });

  function handleAccept(e: ChangeEvent<HTMLInputElement>) {
    setAccept(e.target.checked);
  }

  async function handleSubmit() {
    const readOnly = localStorage.getItem('readOnly');

    if (!user) return;
    if (!readOnly) setPageLoading(true);
    setIsOpen(false);
    setIsAccept(true);
    setStorageItem('accept', 'true');

    await actionLog('me', 'accept_rules', {
      task,
      actions,
      recognition,
    });

    const sender = getStorageItem('jogajuntobb_invite_code');

    if (typeof sender === 'string') {
      const hasInvite = await checkInvite(user._id, sender);

      if (hasInvite) {
        await actionLog('me', 'accept_invite', { sender });
      }

      removeStorageItem('jogajuntobb_invite_code');
    }

    await refreshUserPromise();

    setPageLoading(false);
  }

  /**
   * Trava do botão do ramo de preferências. Só é chamada de lá, então `showRenounce`
   * é sempre verdadeiro aqui — a guarda que existia para o caso contrário saiu com o
   * ramo do regulamento, que tem botão próprio (`disabled={!accept}`).
   */
  function verifyPreference() {
    if (!accept) return true;

    if ((!task || !recognition || !actions) && !decline) return true;

    return false;
  }

  useEffect(() => {
    // userInfo?.cad_missao === 0 || userInfo?.tipo_ro === 'GERENCIAL' || user?.extra?.tutorial
    if (user && !isAccept) {
      const readOnly = localStorage.getItem('readOnly');
      const _accept = localStorage.getItem('accept');

      if (readOnly && _accept) {
        setIsOpen(false);
        return;
      }

      try {
        setIsOpen(!user.extra.accept);

        if (!user.extra.accept) {
          loadData();
        }
      } catch (e) {
        setIsOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userInfo, isAccept]);

  async function loadData() {
    const data = await getRegulation(user?.extra?.rede);
    if (data) {
      setRegulation(data);
    }
  }

  const openCycle = useMemo(
    () => realCycle?.find((item: any) => item.status === 'open'),
    [realCycle]
  );

  const currentCycle = useMemo(() => {
    if (!realCycle) return undefined;

    return openCycle ?? realCycle[0];
  }, [realCycle, openCycle]);

  const badge = cycleBadge(currentCycle?.title);

  function getText(): string {
    const parts = [];
    if (!recognition) parts.push('Reconhecimento');
    if (!task) parts.push('Realizações');
    if (!actions) parts.push('Ações de Engajamento');

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts.join(' e ');

    return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
  }

  return (
    <>
      {showRenounce ? (
        <ModalFrame
          isOpen={modalIsOpen}
          title="Minhas preferências"
          badge={badge}
          footer={
            <>
              <AgreementCheckbox onChange={handleAccept}>
                Concordo com os{' '}
                <a
                  className="font-bold text-brand-blue-dark underline"
                  href={regulation?.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  termos e condições do regulamento
                </a>{' '}
                das preferências selecionadas.
              </AgreementCheckbox>
              <FooterActions>
                <Button variant="secundario" size="sm" className="flex-1 sm:flex-none" onClick={() => signOut()}>
                  Cancelar
                </Button>
                <Button
                  variant="principal"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  disabled={verifyPreference()}
                  onClick={() => handleSubmit()}
                >
                  Salvar preferências
                </Button>
              </FooterActions>
            </>
          }
        >
          {/*
            DOIS CONTEÚDOS, ESCOLHIDOS PELO PERFIL — não dois passos (PREF-04.2,
            revista em 2026-08-07 por decisão do usuário).

            Quem renuncia a dinâmica vê as preferências: renúncia + canais.
            Quem não renuncia — gestor, `cad_missao = 0`, `tipo_ro = GERENCIAL` —
            vê o regulamento e só aceita, exatamente como antes desta feature.

            Entre 05/08 e 07/08 os blocos de canal apareceram para todos, e o
            gestor passou a poder desligar notificação no aceite. Desfeito.

            É ramo por perfil, e não a máquina de `step` que a AD-012 removeu:
            aquele encadeamento ficou inalcançável quando `setStep(1)` virou a
            única chamada, e trazê-lo de volta reintroduziria o código morto sem
            necessidade — ninguém percorre os dois conteúdos, cada perfil vê um.
          */}
          <div className="space-y-4 px-5 py-6 text-jj-body-sm leading-jj-relaxed text-content lg:px-7">
            <p className="m-0 text-sm">
              O Joga Junto possui 3 dinâmicas que você participa. Caso você não queira participar de alguma delas,
              desmarque a dinâmica e confirme o termo de recusa. Você pode modificar essas preferências a qualquer momento
              até {currentCycle?.date_end && format(new Date(currentCycle?.date_end), 'dd/MM/yyyy', { locale: ptBR })}
            </p>

            <fieldset className="m-0 space-y-2 border-0 p-0">
              <legend className="sr-only">Dinâmicas</legend>
              <DynamicCheckbox name="acceptRecognition" checked={recognition} onChange={setRecognition}>
                Dinâmica de Reconhecimento
              </DynamicCheckbox>
              <DynamicCheckbox name="acceptTask" checked={task} onChange={setTask}>
                Dinâmica de Realizações
              </DynamicCheckbox>
              <DynamicCheckbox name="acceptChallenge" checked={actions} onChange={setActions}>
                Dinâmica de Ações de Engajamento (Ações Ecoa, Desafio Semestral e Bônus de colaboração)
              </DynamicCheckbox>
            </fieldset>

            {/* D3: raio de 10px e 11px não têm degrau no Tailwind. */}
            {(!recognition || !actions || !task) && (
              <div className="space-y-2 rounded-[10px] bg-brand-yellow-light p-4">
                <p className="m-0 font-bold text-brand-blue-dark">
                  Conforme Solicitação de Exclusão do Programa Joga Junto BB, os pontos obtidos nessa dinâmica não serão
                  convertidos em premiação.
                </p>
                <label className="flex cursor-pointer items-center gap-[11px]">
                  <input
                    type="checkbox"
                    name="acceptRecognitionDecline"
                    className={CHECKBOX}
                    checked={decline}
                    onChange={e => setDecline(e.target.checked)}
                  />
                  Confirmo que estou ciente do termo de renúncia abaixo.
                </label>

                <b className="block text-brand-blue-dark">SOLICITAÇÃO DE EXCLUSÃO DO PROGRAMA JOGA JUNTO BB</b>
                <p className="m-0">
                  Solicito, para os devidos fins, minha exclusão do Programa Joga Junto BB referente ao Ciclo{' '}
                  {currentCycle?.title}.
                </p>
                <p className="m-0">
                  Estou ciente de que minha solicitação de exclusão na(s) Dinâmica(s) ({`${getText()}`}), é irrevogável e
                  irretratável e implica na perda do meu direito a participar das premiações oferecidas no Programa Joga
                  Junto BB durante o semestre acima citado.
                </p>
              </div>
            )}
          </div>
        </ModalFrame>
      ) : (
        /*
          O aceite de quem não escolhe nada — restaurado com o texto literal
          que existia antes da feature (ver o diff de `bd634131`). Sem os
          blocos de canal, o conteúdo anterior ficaria com o título "Minhas
          Preferências" sobre uma tela vazia, um botão "Salvar Preferências"
          que não salva escolha nenhuma, e um termo falando em "preferências
          selecionadas" que ninguém selecionou.
        */
        <ModalFrame
          isOpen={modalIsOpen}
          size="lg"
          title="Regulamento"
          subtitle="Leia as regras do ciclo e confirme sua participação para continuar."
          badge={badge}
          footer={
            <>
              <AgreementCheckbox onChange={handleAccept}>
                Concordo com os termos e condições do regulamento do{' '}
                <b className="text-brand-blue-dark">#JogaJuntoBB</b>
              </AgreementCheckbox>
              <FooterActions>
                <Button variant="secundario" size="sm" className="flex-1 sm:flex-none" onClick={() => signOut()}>
                  Sair do #JogaJuntoBB
                </Button>
                <Button
                  variant="principal"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  disabled={!accept}
                  onClick={() => handleSubmit()}
                >
                  Confirmar participação
                </Button>
              </FooterActions>
            </>
          }
        >
          <RegulationReader html={regulation?.regulation_text} />
        </ModalFrame>
      )}

      {pageLoading && (
        <div className="fixed z-40 w-full h-full top-0 left-0 block flex items-center justify-center bg-black bg-opacity-60">
          <div role="status" className="flex items-center justify-center flex-col space-y-2 text-white">
            <svg
              aria-hidden="true"
              className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="text-white">Atualizando Preferências...</span>
          </div>
        </div>
      )}
    </>
  );
}
